import logger from '@/lib/logger';

interface PerformanceMetrics {
  lcp: number;
  fid: number;
  cls: number;
  ttfb: number;
  inp: number;
}

const PERFORMANCE_BUDGETS = {
  lcp: 2500, // Largest Contentful Paint (ms)
  fid: 100, // First Input Delay (ms)
  cls: 0.1, // Cumulative Layout Shift
  ttfb: 800, // Time to First Byte (ms)
  inp: 200, // Interaction to Next Paint (ms)
  jsSize: 150 * 1024, // 150KB JS budget per page
  imageSize: 200 * 1024, // 200KB image budget per image
  totalBundleSize: 350 * 1024, // 350KB total JS bundle budget
};

export function reportWebVitals(metric: any) {
  const value = metric.value;
  const name = metric.name.toLowerCase();

  if (Object.keys(PERFORMANCE_BUDGETS).includes(name)) {
    if (value > PERFORMANCE_BUDGETS[name as keyof typeof PERFORMANCE_BUDGETS]) {
      logger.warn(
        `Performance budget exceeded for ${name}: ${value} (budget: ${
          PERFORMANCE_BUDGETS[name as keyof typeof PERFORMANCE_BUDGETS]
        })`
      );
    }
  }
}

export function monitorImageSize(src: string, size: number) {
  if (size > PERFORMANCE_BUDGETS.imageSize) {
    logger.warn(
      `Image size budget exceeded for ${src}: ${size}B (budget: ${PERFORMANCE_BUDGETS.imageSize}B)`
    );
  }
}

export function monitorBundleSize(bundleSize: number) {
  if (bundleSize > PERFORMANCE_BUDGETS.totalBundleSize) {
    logger.warn(
      `Total bundle size budget exceeded: ${bundleSize}B (budget: ${PERFORMANCE_BUDGETS.totalBundleSize}B)`
    );
  }
}

export function createPerformanceObserver() {
  if (typeof window === 'undefined') return;

  const observer = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
      if (entry.entryType === 'resource') {
        const size = (entry as PerformanceResourceTiming).encodedBodySize;
        if (entry.name.endsWith('.js')) {
          if (size > PERFORMANCE_BUDGETS.jsSize) {
            logger.warn(
              `JS file size budget exceeded for ${entry.name}: ${size}B (budget: ${PERFORMANCE_BUDGETS.jsSize}B)`
            );
          }
        }
      }
    });
  });

  observer.observe({ entryTypes: ['resource', 'navigation', 'longtask'] });
  return observer;
}

export function monitorInteractions() {
  if (typeof window === 'undefined') return;

  let lastInteractionTime = performance.now();

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      const currentTime = performance.now();
      const timeSinceLastInteraction = currentTime - lastInteractionTime;

      if (timeSinceLastInteraction > PERFORMANCE_BUDGETS.inp) {
        logger.warn(
          `Slow interaction detected: ${timeSinceLastInteraction.toFixed(2)}ms`
        );
      }

      lastInteractionTime = currentTime;
    }
  });

  observer.observe({ entryTypes: ['event'] });
  return observer;
}
